const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    carousel.on('created', (carousel) => {
      carousel.container.setAttribute(`data-initialised`, true)
    })
  }
}

const useGrabCursor = () => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const container = carousel.container

    container.setAttribute(`data-grab-cursor-initialised`, true)

    const setGrabbing = () => {
      if (!carousel.options.disabled) {
        container.setAttribute(`data-cursor-grabbing`, true)
      }
    }

    const unsetGrabbing = () => {
      container.setAttribute(`data-cursor-grabbing`, false)
    }

    const attachCursorEvents = () => {
      container.addEventListener('mousedown', setGrabbing)
      container.addEventListener('mouseup', unsetGrabbing)
      container.addEventListener('mouseleave', unsetGrabbing)
    }

    const detachCursorEvents = () => {
      container.removeEventListener('mousedown', setGrabbing)
      container.removeEventListener('mouseup', unsetGrabbing)
      container.removeEventListener('mouseleave', unsetGrabbing)
    }

    carousel.on('created', () => {
      attachCursorEvents()
    })

    carousel.on('optionsChanged', () => {
      if (carousel.options.disabled) {
        container.setAttribute(`data-cursor-grabbing`, false)
      }
    })

    carousel.on('destroyed', () => {
      container.setAttribute(`data-cursor-grabbing`, false)
      detachCursorEvents()
    })
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} elements
 */
const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details

      if (next) {
        next.disabled = abs === maxIdx
        next.setAttribute('aria-hidden', maxIdx === 0)
      }

      if (previous) {
        previous.disabled = abs === min
        previous.setAttribute('aria-hidden', maxIdx === 0)
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
  }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {
    const updateDotStates = () => {
      dots.forEach((dot, index) => {
        const isCurrent = carousel.track.details.rel === index

        dot.setAttribute('aria-current', String(isCurrent))
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

window.BAO.Carousel = window.BAO.Carousel || {}
window.BAO.Carousel.plugins = {
  setInitialised,
  useGrabCursor,
  useArrows,
  useAutoplay,
  useDots,
}
